import { http } from '@/lib/axios';
import { unwrap, toApiError } from '@/lib/unwrap';
import type { ApiEnvelope, SearchParams } from '@/types/api';
import type { Bundle, BundleItem } from '@/types/coupon';

/** A populated product ref the backend may inline on a bundle item. */
interface RawProductRef {
  _id?: string;
  id?: string;
  name?: string;
  price?: number;
  /** Backend populates this on bundle items so the FE knows which components
   *  need a slot pick (see BundleService.searchActiveAndValid populate). */
  requiresTimeSlot?: boolean;
}

interface RawBundleItem {
  productId?: string | RawProductRef;
  name?: string;
  productName?: string;
  price?: number;
  productPrice?: number;
  quantity?: number;
  requiresTimeSlot?: boolean;
}

interface RawBundle {
  id?: string;
  _id?: string;
  name?: string;
  description?: string;
  bundleType?: string;
  userBundleType?: string;
  /** Pricing — finalPrice is the virtual; bundlePrice the stored FIXED_PRICE. */
  finalPrice?: number;
  bundlePrice?: number;
  price?: number;
  totalOriginalPrice?: number;
  savings?: number;
  minItems?: number;
  maxItems?: number;
  items?: RawBundleItem[];
  bundleItems?: RawBundleItem[];
  coverPicture?: { url?: string } | null;
  imageUrl?: string | null;
  outOfStock?: boolean;
}

function refField<K extends keyof RawProductRef>(
  ref: string | RawProductRef | undefined,
  key: K,
): RawProductRef[K] | undefined {
  return ref && typeof ref === 'object' ? ref[key] : undefined;
}

function normalizeItem(raw: RawBundleItem): BundleItem {
  const pid =
    typeof raw.productId === 'object' && raw.productId
      ? (raw.productId._id ?? raw.productId.id ?? '')
      : (raw.productId ?? '');
  return {
    productId: String(pid),
    name: raw.name ?? raw.productName ?? refField(raw.productId, 'name'),
    price: raw.price ?? raw.productPrice ?? refField(raw.productId, 'price'),
    quantity: raw.quantity ?? 1,
    requiresTimeSlot: Boolean(raw.requiresTimeSlot ?? refField(raw.productId, 'requiresTimeSlot')),
  };
}

function normalizeBundle(raw: RawBundle): Bundle {
  const rawItems = raw.items ?? raw.bundleItems ?? [];
  return {
    id: String(raw.id ?? raw._id ?? ''),
    name: raw.name ?? 'Paket',
    description: raw.description ?? undefined,
    bundleType: raw.bundleType ?? 'FIXED',
    userBundleType: raw.userBundleType,
    price: raw.finalPrice ?? raw.bundlePrice ?? raw.price ?? 0,
    originalPrice: raw.totalOriginalPrice,
    savings: raw.savings,
    minItems: raw.minItems,
    maxItems: raw.maxItems,
    items: Array.isArray(rawItems) ? rawItems.map(normalizeItem) : [],
    imageUrl: raw.coverPicture?.url ?? raw.imageUrl ?? null,
    outOfStock: raw.outOfStock ?? false,
  };
}

/**
 * Active, currently-sellable bundles for the POS catalog. Uses `/bundle/active`
 * (ACTIVE + within validity window) rather than `/bundle` which also lists
 * inactive/expired ones. The list payload may arrive either as a bare array or
 * nested under `{ data: [...] }` (paginated), so we extract defensively.
 */
export async function getBundles(params?: SearchParams): Promise<Bundle[]> {
  try {
    const res = await http.get<ApiEnvelope<unknown>>('/bundle/active', { params });
    const data = unwrap(res.data) as unknown;
    const list = Array.isArray(data)
      ? data
      : Array.isArray((data as { data?: unknown })?.data)
        ? ((data as { data: unknown[] }).data)
        : [];
    return (list as RawBundle[]).map(normalizeBundle).filter((b) => b.id);
  } catch (error) {
    throw toApiError(error);
  }
}

/** A bundle price resolved for a specific visit date (PricingSchedule-aware). */
export interface ResolvedBundlePrice {
  price: number;
  originalPrice?: number;
  savings?: number;
}

/**
 * POST /bundle/resolve-prices — PricingSchedule-aware bundle prices for a visit
 * date (component rate plans included), keyed by bundle id. Keeps the displayed
 * bundle price in sync with what the sale will actually charge. Fail-open.
 */
export async function resolveBundlePrices(
  bundleIds: string[],
  date: string,
): Promise<Record<string, ResolvedBundlePrice>> {
  if (bundleIds.length === 0) return {};
  try {
    const res = await http.post<ApiEnvelope<{ prices?: Record<string, RawBundle & { totalOriginalPrice?: number }> }>>(
      '/bundle/resolve-prices',
      { bundleIds, date },
    );
    const data = unwrap(res.data) as { prices?: Record<string, { price?: number; totalOriginalPrice?: number; savings?: number }> };
    const out: Record<string, ResolvedBundlePrice> = {};
    for (const [id, p] of Object.entries(data?.prices ?? {})) {
      out[id] = {
        price: Number(p.price) || 0,
        originalPrice: typeof p.totalOriginalPrice === 'number' ? p.totalOriginalPrice : undefined,
        savings: typeof p.savings === 'number' ? p.savings : undefined,
      };
    }
    return out;
  } catch {
    // Never block the catalog on a pricing lookup — fall back to base prices.
    return {};
  }
}
